import { useState } from 'react'

import { FvButton, useFvNavigate } from '@fv/client-components'
import type { Quote } from '@fv/client-types'

import { FooterBar } from '@/components/FooterBar'
import InfoBox from '@/components/InfoBox'
import { PillHeader } from '@/components/PillHeader'
import { Price } from '@/components/shared/Price'
import { TruckloadAwardConfirmation } from '@/components/TruckloadAwardConfirmation'
import { routes } from '@/routes'

import { TLBookingBolField } from '../book/BookingFormBolField'
import { TLBookingFormShare } from '../book/BookingFormShare'
import { TLBookingFormTags } from '../book/BookingFormTags'
import { isDraftLoadId } from '../customer-routed/draftLoadId'
import { QuoteReasonSlider } from '../quote-reason/QuoteReasonForm'
import { BookingBranding } from './BookingBranding'
import { BookingCommodities } from './BookingCommodities'
import { BookingCustomsBroker } from './BookingCustomsBroker'
import { BookingFormCustomerRouted } from './BookingFormCustomerRouted'
import { BookingFormGLCode } from './BookingFormGLCode'
import { BookingLoad } from './BookingLoad'
import { BookingLocations } from './BookingLocations'
import { EmergencyContact } from './EmergencyContact'
import { useBookingForm, useBookingPageNavigation } from './hooks'
import { PaymentTerms } from './PaymentTerms'
import { PickupOnlyPrompt } from './PickupOnlyPrompt'
import type { BookFormLoad } from './types'

const infoBoxClassName = 'mt-3 mb-4'

export const BookingForm = () => {
  const navigate = useFvNavigate()
  const {
    bookShipment,
    customsBrokers,
    hasPickupOnlyPrompt,
    isBooking,
    isCustomerRouted,
    isPickupOnly,
    isRetender,
    loads,
    onPickupOnlyChange,
    pickup,
    requireQuoteReason,
    quoteSelectionReason,
    selectedQuotes,
  } = useBookingForm()

  const firstLoad = loads[0]
  const [hasSubmittedQuoteReason, setHasSubmittedQuoteReason] = useState(false)
  const { leave } = useBookingPageNavigation()
  const isEditing = firstLoad.status !== 'pending' && pickup?.status !== 'error'
  const isAwardingCustomerRouted =
    isCustomerRouted && !isDraftLoadId(firstLoad.loadId)

  const buttonLabel = isAwardingCustomerRouted
    ? 'Award this shipment'
    : isCustomerRouted
      ? 'Create customer routed shipment'
      : isEditing
        ? 'Update this shipment'
        : 'Book this shipment'

  const firstQuote = selectedQuotes.find(q => q._id === firstLoad.quoteId)

  return (
    <>
      <div className="overflow-auto p-8 bg-white relative min-h-0 flex-1">
        <BookingHeader
          firstQuote={firstQuote}
          isAwardingCustomerRouted={isAwardingCustomerRouted}
          isCustomerRouted={isCustomerRouted}
          isRetender={isRetender}
          loads={loads}
        />

        {isRetender && (
          <InfoBox className={infoBoxClassName} icon="exclamation-triangle">
            {firstQuote?.providerName} requires handling unit and commodity
            information to book this shipment.
          </InfoBox>
        )}

        {hasPickupOnlyPrompt && (
          <PickupOnlyPrompt
            checked={isPickupOnly}
            className={infoBoxClassName}
            onChange={onPickupOnlyChange}
          />
        )}

        {pickup?.status === 'error' && (
          <InfoBox className={infoBoxClassName} icon="exclamation-triangle">
            Booking failed: {pickup.error ?? 'Unknown error.'}
          </InfoBox>
        )}

        {isCustomerRouted && <BookingFormCustomerRouted />}
        <BookingLocations />
        <BookingCommodities />

        {loads.map(load => (
          <BookingLoad key={load.loadId} load={load} />
        ))}

        {customsBrokers.map((b, i) => (
          <BookingCustomsBroker key={i} index={i} />
        ))}
        <PaymentTerms />
        <BookingBranding />
        <EmergencyContact />

        {loads.length === 1 && (
          <div className="gray-container px-4 pb-2 pt-3">
            <div className="flex gap-2">
              <TLBookingBolField className="flex-1 min-w-0 max-w-[25%]" />
              <TLBookingFormShare className="flex-1 min-w-[400px]" />
              <TLBookingFormTags className="flex-1 min-w-0" />
              <BookingFormGLCode className="flex-1 min-w-0" />
            </div>
          </div>
        )}
      </div>

      <FooterBar jc className="shrink-0">
        {isEditing && !isCustomerRouted && (
          <FvButton
            icon="arrow-to-left"
            theme="default"
            onClick={() => {
              leave(loads, true)
            }}
            type="button"
          >
            <span>Go back</span>
          </FvButton>
        )}

        <FvButton
          className=""
          onClick={bookShipment}
          theme="primary"
          type="button"
          fwd
          icon={isBooking ? 'spinner' : 'check'}
        >
          <span>{buttonLabel}</span>
        </FvButton>
      </FooterBar>
      {loads.length === 1 && firstQuote && !isCustomerRouted && (
        <QuoteReasonSlider
          load={{
            loadId: firstLoad.loadId,
            workflow: 'truckload',
            quoteRequestId: firstLoad.quoteRequestId,
            status: firstLoad.status,
            quoteSelectionReason,
          }}
          quote={firstQuote}
        />
      )}
      {loads.length > 1 && requireQuoteReason && !isCustomerRouted && (
        <TruckloadAwardConfirmation
          isOpen={!hasSubmittedQuoteReason}
          title=""
          isBookingPage={true}
          onCancel={() => {
            navigate(
              routes.rates({
                loadId: firstLoad.loadId,
                quoteRequestId: firstLoad.quoteRequestId,
                workflow: 'truckload',
              }),
            )
          }}
          cancelText="Go back to my rates"
          onSubmit={() => setHasSubmittedQuoteReason(true)}
          submitLabel="Submit and proceed"
          loadsToBook={loads}
        />
      )}
    </>
  )
}

type BookingHeaderProps = {
  firstQuote?: Quote
  isAwardingCustomerRouted: boolean
  isCustomerRouted: boolean
  isRetender: boolean
  loads: BookFormLoad[]
}

const BookingHeader = ({
  firstQuote,
  isAwardingCustomerRouted,
  isCustomerRouted,
  isRetender,
  loads,
}: BookingHeaderProps) => {
  const isMultiTruck = loads.length > 1
  const firstLoad = loads[0]
  const carrier = firstQuote?.providerName
  const status = firstLoad.status

  function composeHeaderText() {
    if (isMultiTruck) return 'This is a multi-truck shipment.'
    if (firstLoad.isFreightCollect) return `Load booked with ${carrier}`
    if (status === 'pending') return `Book this load with ${carrier} for `
    if (isRetender) return `Retendering this load to ${carrier} for `
    return `Load booked with ${carrier} for `
  }

  const headerText = isAwardingCustomerRouted
    ? 'Award this customer routed shipment'
    : isCustomerRouted
      ? 'Create a customer routed shipment'
      : composeHeaderText()

  return (
    <PillHeader st className="beer-container mb-2 -ml-2">
      <h6 className="mb-0 inline-flex items-center gap-1">
        <span>{headerText}</span>

        {!isCustomerRouted &&
          !isMultiTruck &&
          firstQuote &&
          !firstLoad.isFreightCollect && (
            <Price
              className="price"
              amount={firstQuote.amount}
              currency={firstQuote.currency}
            />
          )}
      </h6>
    </PillHeader>
  )
}
